import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Loading,
  Modal,
  PageHeader,
  TextInput,
} from "../../components/ui";
import { API_URL, api, errorMessage, qs } from "../../lib/api";
import { brl, dateBR, monthLabel } from "../../lib/format";

interface FiscalData {
  month: string;
  items: {
    paymentId: string;
    platformCents: number;
    grossCents: number;
    contractorName: string;
    cpfMasked: string | null;
    date: string;
    issued: { number: string; issuedAt: string } | null;
  }[];
}

/** Notas a emitir da plataforma (a das aulas é do professor). Emissão manual nesta versão. */
export function AdminFiscal() {
  const qc = useQueryClient();
  const [month, setMonth] = useState("");
  const q = useQuery({
    queryKey: ["fiscal", month],
    queryFn: () =>
      api.get<FiscalData>(`/v1/admin/fiscal-notes${qs({ month: month || undefined })}`),
  });
  const [marking, setMarking] = useState<string | null>(null);
  const [note, setNote] = useState({ number: "", issuedAt: new Date().toISOString().slice(0, 10) });
  const mark = useMutation({
    mutationFn: () => api.post(`/v1/admin/fiscal-notes/${marking}/issued`, note),
    onSuccess: () => {
      setMarking(null);
      qc.invalidateQueries({ queryKey: ["fiscal"] });
    },
  });
  const pending = q.data?.items.filter((i) => !i.issued) ?? [];
  return (
    <>
      <PageHeader
        title="Notas a emitir"
        subtitle="Parte da plataforma ByFluent de cada pagamento. A nota das aulas é emitida pelo professor."
        actions={
          <a className="btn ghost" href={`${API_URL}/v1/admin/conversions/google.csv`}>
            Conversões para o Google Ads
          </a>
        }
      />
      <div className="row" style={{ marginBottom: 16 }}>
        <TextInput
          label="Mês"
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
        />
      </div>
      {q.isLoading ? <Loading /> : null}
      {q.error ? <Alert kind="risk">{errorMessage(q.error)}</Alert> : null}
      {q.data ? (
        <Card title={`${monthLabel(q.data.month)}: ${pending.length} pendente(s)`}>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Contratante</th>
                  <th>CPF</th>
                  <th className="num">Plataforma</th>
                  <th className="num">Bruto</th>
                  <th>Pago em</th>
                  <th>Nota</th>
                </tr>
              </thead>
              <tbody>
                {q.data.items.map((i) => (
                  <tr key={i.paymentId}>
                    <td>{i.contractorName}</td>
                    <td className="small">{i.cpfMasked}</td>
                    <td className="num">{brl(i.platformCents)}</td>
                    <td className="num">{brl(i.grossCents)}</td>
                    <td>{dateBR(i.date)}</td>
                    <td>
                      {i.issued ? (
                        <Badge color="green">nº {i.issued.number}</Badge>
                      ) : (
                        <Button
                          size="small"
                          variant="secondary"
                          onClick={() => setMarking(i.paymentId)}
                        >
                          Marcar como emitida
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : null}
      {marking ? (
        <Modal title="Nota emitida" onClose={() => setMarking(null)}>
          <div className="stack">
            <TextInput
              label="Número"
              value={note.number}
              onChange={(e) => setNote({ ...note, number: e.target.value })}
            />
            <TextInput
              label="Data"
              type="date"
              value={note.issuedAt}
              onChange={(e) => setNote({ ...note, issuedAt: e.target.value })}
            />
            {mark.error ? <Alert kind="risk">{errorMessage(mark.error)}</Alert> : null}
            <Button loading={mark.isPending} disabled={!note.number} onClick={() => mark.mutate()}>
              Salvar
            </Button>
          </div>
        </Modal>
      ) : null}
    </>
  );
}
